import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { FaCar, FaSearch, FaStamp, FaShoppingCart, FaMapMarkerAlt, FaPhone, FaWhatsapp } from 'react-icons/fa'

const About = () => {
  const { t } = useTranslation()

  const serviceItems = [
    { icon: FaCar, text: 'Kfz-Zulassungsdienst (Neuzulassung, Ummeldung, Abmeldung)', gradient: 'from-blue-500 to-blue-600' },
    { icon: FaSearch, text: 'Wunschkennzeichen online reservieren', gradient: 'from-purple-500 to-purple-600' },
    { icon: FaStamp, text: 'Kennzeichenprägung vor Ort', gradient: 'from-emerald-500 to-emerald-600' },
    { icon: FaShoppingCart, text: 'Online-Shop für Kennzeichen und Zubehör', gradient: 'from-orange-500 to-orange-600' },
    { icon: FaMapMarkerAlt, text: 'Persönliche Beratung vor Ort in Göttingen', gradient: 'from-red-500 to-red-600' },
  ]

  return (
    <div className="bg-dark-950 min-h-screen py-12 relative overflow-hidden">
      <div className="fixed inset-0 bg-grid-pattern opacity-20"></div>
      <div className="absolute inset-0 bg-gradient-mesh opacity-30"></div>

      <div className="container-custom relative z-10">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h1 className="text-4xl md:text-5xl font-black mb-4">
              <span className="text-gradient">{t('about.title')}</span>
            </h1>
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-primary-500 to-transparent mx-auto"></div>
          </motion.div>

          <div className="space-y-8">
            {/* Company */}
            <motion.section
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="card-hover"
            >
              <h2 className="text-2xl font-bold mb-4 text-gradient">{t('about.company')}</h2>
              <p className="text-base text-gray-300 leading-relaxed">
                {t('about.companyDesc')}
              </p>
            </motion.section>

            {/* Vision */}
            <motion.section
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="card-hover"
            >
              <h2 className="text-2xl font-bold mb-4 text-gradient">{t('about.vision')}</h2>
              <p className="text-base text-gray-300 leading-relaxed">
                {t('about.visionDesc')}
              </p>
            </motion.section>

            {/* Values */}
            <motion.section
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="card-hover"
            >
              <h2 className="text-2xl font-bold mb-4 text-gradient">{t('about.values')}</h2>
              <p className="text-base text-gray-300 leading-relaxed">
                {t('about.valuesDesc')}
              </p>
            </motion.section>

            {/* Services */}
            <motion.section
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="card-hover"
            >
              <h2 className="text-2xl font-bold mb-6 text-gradient">{t('about.services')}</h2>
              <div className="space-y-4">
                {serviceItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 + index * 0.1, duration: 0.4 }}
                    className="flex items-center gap-4 p-3 rounded-xl bg-dark-800/40 hover:bg-dark-800/60 transition-colors"
                  >
                    <div className={`p-2.5 rounded-lg bg-gradient-to-br ${item.gradient} text-white flex-shrink-0`}>
                      <item.icon className="w-4 h-4" />
                    </div>
                    <span className="text-base text-gray-300">{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.section>

            {/* Location + CTA */}
            <motion.section
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="card-hover text-center"
            >
              <h2 className="text-2xl font-bold mb-4 text-gradient">Besuchen Sie uns</h2>
              <p className="text-gray-400 mb-2">KennzeichenPro24 GmbH</p>
              <p className="text-gray-300 font-medium">Zollstock 15, 37081 Göttingen-Grone</p>
              <p className="text-gray-400 mb-6">Niedersachsen, Deutschland</p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/contact"
                  className="px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-bold rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-300 shadow-lg shadow-primary-500/25"
                >
                  Kontakt aufnehmen
                </Link>
                <a
                  href="tel:+4955149203480"
                  className="px-6 py-3 bg-dark-800 text-gray-200 font-bold rounded-xl hover:bg-dark-700 border border-dark-600 transition-all duration-300 flex items-center gap-2"
                >
                  <FaPhone className="w-3.5 h-3.5" />
                  <span>Anrufen</span>
                </a>
                <a
                  href="https://wa.me/4915565553294"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition-all duration-300 flex items-center gap-2 shadow-lg shadow-green-600/25"
                >
                  <FaWhatsapp className="w-3.5 h-3.5" />
                  <span>WhatsApp</span>
                </a>
              </div>
            </motion.section>
          </div>
        </div>
      </div>
    </div>
  )
}

export default About
